import { useState, useEffect } from "react";
import { isAppContext } from "@/lib/appContext";

interface HeroBackgroundProps {
  isMobile: boolean;
  heroPoster: string;
  heroPosterMobile: string;
  // Current size of the scroll-jacked hero card. Used to size the video iframe
  // so it cover-crops the card identically to the poster's object-cover.
  cardWidth: number;
  cardHeight: number;
}

// The background clip is 16:9. The poster <img> uses object-cover so it always
// crops-to-fill the card at any aspect ratio, but an <iframe> can't be
// object-cover'd — a 16:9 Vimeo player letterboxes once the card is no longer
// 16:9 (which it becomes as the card scroll-expands toward the viewport's
// aspect). Sizing the iframe as a 16:9 box that covers the card keeps the video
// and the poster locked to the same crop at every scroll position.
const VIDEO_ASPECT = 16 / 9;

const HeroBackground = ({ isMobile, heroPoster, heroPosterMobile, cardWidth, cardHeight }: HeroBackgroundProps) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isDataSaver, setIsDataSaver] = useState(false);

  // Detect Data Saver mode (only relevant for desktop)
  useEffect(() => {
    if (isMobile) return;
    const connection = (navigator as Navigator & { connection?: { saveData?: unknown } }).connection;
    if (connection?.saveData) {
      setIsDataSaver(true);
    }
  }, [isMobile]);

  // Build Vimeo URL with adaptive quality (desktop only)
  const getVimeoUrl = () => {
    const baseUrl = "https://player.vimeo.com/video/1152927597?background=1&autoplay=1&loop=1&muted=1&dnt=1";

    if (isDataSaver) {
      return `${baseUrl}&quality=360p&max_quality=720p`;
    }

    return `${baseUrl}&quality=auto`;
  };

  // 16:9 box that covers the current card (center-cropped), mirroring how the
  // poster's object-cover fills the same card.
  const cardAspect = cardHeight > 0 ? cardWidth / cardHeight : VIDEO_ASPECT;
  const coverWidth = cardAspect > VIDEO_ASPECT ? cardWidth : cardHeight * VIDEO_ASPECT;
  const coverHeight = cardAspect > VIDEO_ASPECT ? cardWidth / VIDEO_ASPECT : cardHeight;

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {/* Poster image — a real <picture> so the browser (not JS) picks the
          viewport variant before first paint, matching the media-split
          <link rel="preload"> in index.html. Selecting the variant via
          isMobile (false on the first render) previously made mobile fetch
          the 337KB desktop poster, then re-render to the 52KB mobile one —
          downloading both. The explicit width/height keep CLS at zero. */}
      <picture>
        <source media="(max-width: 767px)" srcSet={heroPosterMobile} width={1918} height={1075} />
        <img
          src={heroPoster}
          alt=""
          width={1823}
          height={1025}
          fetchPriority="high"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover ${isMobile ? 'animate-ken-burns' : ''}`}
        />
      </picture>

      {/* Only show loading spinner and video on desktop */}
      {!isMobile && !isAppContext() && (
        <>
          {/* Loading spinner - show until video ready */}
          {!videoLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            </div>
          )}

          {/* Video iframe — sized as a 16:9 cover box and centered so it crops to
              fill the card exactly like the poster, instead of letterboxing. */}
          <iframe
            src={getVimeoUrl()}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: `${coverWidth}px`,
              height: `${coverHeight}px`,
            }}
            className={`transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
            frameBorder="0"
            allow="autoplay; fullscreen"
            title="Background Video"
            onLoad={() => setVideoLoaded(true)}
          />
        </>
      )}

      {/* Semi-transparent black overlay for legibility of foreground text */}
      <div className="absolute inset-0 bg-black/70 pointer-events-none" />
    </div>
  );
};

export default HeroBackground;
